import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  formatTimestamp,
  eventImage,
  getHeaderImage,
  getBtvFallbackForBg,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Flat = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  startIntro,
  setStartIntro,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const originalHeading = customizations?.headingTextColor || parseColor(themeColors.headingTextColor);
  const heading = getRelativeContrastColor(originalHeading, accent);
  const secondaryAccent = resolveColor(themeColors.secondaryAccentColor, customizations?.secondaryAccentColor);
  const secondaryBg = resolveColor(themeColors.secondaryBackgroundColor, customizations?.secondaryBackgroundColor);
  const body = resolveColor(themeColors.bodyTextColor, customizations?.bodyTextColor);
  const buttonText = resolveColor(
    themeColors.buttonTextColor ?? themeColors.bodyTextColor,
    customizations?.buttonTextColor
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Tahoma, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Verdana, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || 'You have been invited to:');
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _startIntro = startIntro ?? (customizations?.startIntro || 'The show starts on');
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Flat"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--secondary-accent': secondaryAccent,
          '--secondary-bg': secondaryBg,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)', border: '1px solid var(--body)' }}
            >
              <tbody>
                {/* Logo header */}
                <tr>
                  <td
                    align="center"
                    valign="middle"
                    height={48}
                    style={{ background: 'var(--container)', borderBottom: '1px solid var(--body)', padding: '8px' }}
                  >
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Hero image */}
                <tr>
                  <td
                    style={{
                      background: 'var(--secondary-bg)',
                      borderBottom: '1px solid var(--body)',
                      fontSize: 0,
                      lineHeight: '0',
                    }}
                  >
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={520} src={bodyImg} style={{ display: 'block' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Invite label */}
                <tr>
                  <td
                    style={{
                      background: 'var(--secondary-bg)',
                      borderBottom: '1px solid var(--body)',
                      padding: '16px 8px',
                    }}
                  >
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--body)',
                        fontSize: '16px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        lineHeight: '16px',
                        letterSpacing: '0.48px',
                        wordWrap: 'break-word',
                      }}
                    />
                  </td>
                </tr>

                {/* Title */}
                <tr>
                  <td
                    align="center"
                    style={{ background: 'var(--accent)', borderBottom: '1px solid var(--body)', padding: '24px 8px' }}
                  >
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '48px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        lineHeight: '48px',
                        letterSpacing: '0.96px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>

                {/* Description + date + register */}
                <tr>
                  <td
                    align="center"
                    style={{
                      padding: '40px 24px 24px 24px',
                      borderLeft: '1px solid var(--body)',
                      borderRight: '1px solid var(--body)',
                    }}
                  >
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ maxWidth: '294px' }}>
                      <tbody>
                        {sessionDate && (
                          <>
                            <tr>
                              <td align="center" style={{ padding: '4px 8px 2px 8px' }}>
                                <ContentEditable
                                  value={_startIntro}
                                  setValue={setStartIntro}
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '12px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '16px',
                                    letterSpacing: '0.36px',
                                    wordWrap: 'break-word',
                                    textAlign: 'center',
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td align="center" style={{ padding: '4px 8px 24px 8px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '15px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    wordWrap: 'break-word',
                                    textAlign: 'center',
                                  }}
                                >
                                  {sessionDate}
                                </div>
                              </td>
                            </tr>
                          </>
                        )}
                        {eventDescription && (
                          <tr>
                            <td align="center" style={{ paddingBottom: '24px' }}>
                              <ContentEditable
                                value={eventDescription}
                                setValue={setDescription}
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 400,
                                  lineHeight: '20px',
                                  letterSpacing: '0.42px',
                                  wordWrap: 'break-word',
                                  textAlign: 'center',
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Register button */}
                <tr>
                  <td
                    align="center"
                    style={{
                      paddingBottom: '40px',
                      borderLeft: '1px solid var(--body)',
                      borderRight: '1px solid var(--body)',
                      borderBottom: '1px solid var(--body)',
                    }}
                  >
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{
                              background: 'var(--secondary-accent)',
                              border: '1px solid var(--body)',
                              width: '148px',
                              height: '40px',
                            }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                padding: '6px 0 6px 0',
                                width: '148px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '13px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '16px',
                                  letterSpacing: '0.39px',
                                  wordWrap: 'break-word',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Calendar links footer */}
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
